import {
  Checkbox,
  type CheckboxProps,
  Divider,
  Flex,
  Menu,
  Pagination,
  type PaginationProps,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconEdit } from "@tabler/icons-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import HistoricalSnapshotButton from "src/components/pages/HistoricalSnapshot/HistoricalSnapshotButton";
import Breadcrumbs from "src/components/shared/Breadcrumbs/Breadcrumbs";
import { DateTimeDisp } from "src/components/shared/DateTime";
import CheckListsButton from "src/generator/LogBar/_Buttons/CheckListsButton";
import FilesButton from "src/generator/LogBar/_Buttons/FilesButton";
import HistoryButton from "src/generator/LogBar/_Buttons/HistoryButton";
import WithdrawButton from "src/generator/LogBar/_Buttons/WithdrawButton";
import { PendingFilesProvider } from "src/generator/LogBar/PendingFilesContext";
import { StyledBurger } from "src/styled";

import VerifyButton from "../VerifyButton";
import VerifyCommentsButton from "./VerifyCommentsButton";
import VerifyMedLogSelect from "./VerifyMedLogSelect";

interface Props {
  paginationProps: PaginationProps;
  checkboxProps: CheckboxProps;
  verifiable: boolean;
  preview?: boolean;
  lbname: string;
  deleted: number;
  status: string;
  unauthorized: boolean;
  isMedLog?: boolean;
  eventStatus?: string;
  lbid?: string;
  entryref?: string;
  entryDate?: string;
  entryDateUTC?: string;
}

const VerifyHeader = ({
  verifiable,
  preview,
  paginationProps,
  checkboxProps,
  lbname,
  unauthorized,
  isMedLog,
  eventStatus,
  lbid,
  entryref,
  entryDate,
  entryDateUTC,
}: Props) => {
  const [opened, { toggle }] = useDisclosure(false);
  const navigate = useNavigate();
  const { idType, lbeidOrPartId } = useParams();
  const [searchParams] = useSearchParams();
  const logbookPath = `/logbook/${lbid}/${idType}/${lbeidOrPartId}`;
  const verifyReturnPath =
    searchParams.get("from") === "entry" ? logbookPath : "/summary";
  const openLogbook = () => {
    navigate(logbookPath);
  };
  return (
    <Flex direction="column" style={{ marginBottom: 5 }}>
      <Breadcrumbs pb={0} border={false} justify="space-between">
        <Flex align="center" gap="xl">
          <Pagination {...paginationProps} />
          <PendingFilesProvider>
            <Checkbox
              labelPosition="left"
              label="Verify"
              disabled={unauthorized || !verifiable}
              {...checkboxProps}
            />

            <VerifyButton
              request="verify-entry"
              navigatePath={verifyReturnPath}
            />
            {!unauthorized && (
              <Menu
                keepMounted
                onClose={toggle}
                shadow="md"
                width={200}
                position="bottom-end">
                <Menu.Target>
                  <StyledBurger opened={opened} onClick={toggle} />
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconEdit size={20} />}
                    onClick={openLogbook}>
                    Edit
                  </Menu.Item>
                  <CheckListsButton />
                  <VerifyCommentsButton />
                  <FilesButton hamburger />
                  <HistoryButton hamburger summary />
                  <HistoricalSnapshotButton
                    lbid={lbid}
                    previewAvailable={preview}
                  />
                  <WithdrawButton hamburger />
                </Menu.Dropdown>
              </Menu>
            )}
          </PendingFilesProvider>
        </Flex>
      </Breadcrumbs>
      <Divider w="100%" my={12} />
      <Flex w="100%" justify="space-between" mb="xl">
        <Flex gap="md">
          <Text fw="bold">{lbname}</Text>
          <Text fw="bold">Entry Ref. #{entryref}</Text>
          {entryDate && (
            <Text fw="bold">
              <DateTimeDisp
                color="blue"
                dir="row"
                utc={entryDateUTC}
                lt={entryDate}
              />
            </Text>
          )}
        </Flex>
        {isMedLog && <VerifyMedLogSelect defaultValue={eventStatus} />}
      </Flex>
    </Flex>
  );
};

export default VerifyHeader;
